import apiService from "../../shared/api/apiService";
import { logger } from "../../shared/utils/logger";

async function registerUser(registrationDetails) {
  try {
    // Convert mobile to mobileNumber and ensure it's properly formatted
    const formattedDetails = {
      name: registrationDetails.name || "",
      email: registrationDetails.email || "",
      mobileNumber: registrationDetails.mobile ? registrationDetails.mobile.replace(/\D/g, '') : null,
      pwd: registrationDetails.password
    };
    
    const apiData = await apiService.post(
      "/api/v1/user/register",
      formattedDetails,
      false
    );

    // For successful registration, return the apiData directly
    // The Signup component will check apiData.isSuccess()
    return apiData;
  } catch (apiData) {
    // For network errors or server errors, throw to trigger enhanced error handling
    // This ensures the Signup component's catch block is executed
    throw apiData;
  }
}

async function resendOtp(username) {
  try {
    const url = `/api/v1/user/login/resend-otp`;
    const payload = { username };
    const apiData = await apiService.post(url, payload, false);
    
    if (apiData.isSuccess()) {
      return { ok: true };
    }
    
    const error = apiData.getError();
    return { ok: false, error: error.message, code: error.code };
  } catch (apiData) {
    const error = apiData.getError();
    return { ok: false, error: error.message, code: error.code };
  }
}

async function loginUser(loginDetails) {
  try {
    // Convert email to username for backend compatibility
    const loginPayload = {
      username: loginDetails.email,
      password: loginDetails.password,
      appSource: "SHOP_APP",
    };

    const apiData = await apiService.post("/api/v1/user/loginUser", loginPayload, false);

    if (apiData.isSuccess()) {
      const data = apiData.getData();
      //const code = data?.code;
      const code = data?.code || null;
      // Get token from axios response headers directly from apiData
      const axiosResponse = apiData.response;
      const token = axiosResponse?.headers?.authorization || axiosResponse?.headers?.Authorization || null;
     // const user = data?.data || null;
      const user = data?.data || data || null;

      // If backend indicates OTP required
      if (code === 'OTP_REQUIRED') {
        return { ok: false, code: 'OTP_REQUIRED', user, apiData };
      }
      // Otherwise expect a token for success
      return { ok: true, token, user, apiData };
    }

    const error = apiData.getError();
    // For HTTP 500+ errors, throw to trigger enhanced error handling
    const rawData = apiData.getRawData();
    if (rawData?.status >= 500) {
      throw apiData;
    }
    return { ok: false, error: error.message, code: error.code, apiData };
  } catch (apiData) {
    // Always throw caught errors to trigger enhanced error handling
    throw apiData;
  } 
}

async function verifyOtp({ username, otp }) {
  try {
    const url = `/api/v1/user/login/verify-otp`;
    const apiData = await apiService.post(url, { username, otp, appSource: "SHOP_APP" }, false);
    
    if (apiData.isSuccess()) {
      const data = apiData.getData();
      // Get token from axios response headers directly from apiData
      const axiosResponse = apiData.response;
      const token = axiosResponse?.headers?.authorization || axiosResponse?.headers?.Authorization || null;
      const user = data?.data || data || null;
      return { ok: true, token, user, apiData };
    }
    
    const error = apiData.getError();
    return { ok: false, error: error.message, code: error.code, apiData };
  } catch (apiData) {
    const error = apiData.getError();
    return { ok: false, error: error.message, code: error.code, apiData };
  }
}

export { registerUser, loginUser, verifyOtp, resendOtp };

// Fetch current authenticated user's profile
async function getCurrentUser() {
  try {
    const apiData = await apiService.get("/api/v1/user/me", true);
    
    if (apiData.isSuccess()) {
      const data = apiData.getData();
      return { ok: true, data: data };
    }
    
    const error = apiData.getError();
    return { ok: false, error: error.message, code: error.code };
  } catch (apiData) {
    const error = apiData.getError();
    return { ok: false, error: error.message, code: error.code };
  }
}

// Update current user's profile. Requires password; OTP for elevated roles.
async function updateProfile({ name, phoneNumber, password, otp }) {
  try {
    const payload = {
      name,
      phoneNumber,
      password,
      otp: otp || null,
    };
    
    const apiData = await apiService.put("/api/v1/user/me", payload, true);
    
    if (apiData.isSuccess()) {
      const data = apiData.getData();
      return { ok: true, data: data };
    }
    
    const error = apiData.getError();
    return { ok: false, error: error.message, code: error.code };
  } catch (apiData) {
    const error = apiData.getError();
    return { ok: false, error: error.message, code: error.code };
  }
}

// Logout current user
async function logoutUser() {
  try {
    logger.info('Calling logout API');
    
    const apiData = await apiService.post("/api/v1/session/logout", {}, true);
    
    if (apiData.isSuccess()) {
      logger.info('Logout API call successful', { 
        service: 'userService', 
        action: 'logout'
      });
      return { ok: true };
    }
    
    const error = apiData.getError();
    logger.warn('Logout API call failed', { 
      service: 'userService', 
      action: 'logout',
      error: error.message,
      code: error.code 
    });
    return { ok: false, error: error.message, code: error.code };
  } catch (apiData) {
    const error = apiData.getError();
    logger.error('Logout API call threw exception', error, { 
      service: 'userService', 
      action: 'logout'
    });
    return { ok: false, error: error.message, code: error.code };
  }
}

// Password reset functions
async function requestPasswordReset(email) {
  try {
    logger.info('Requesting password reset', { email });
    
    const apiData = await apiService.post('/api/v1/auth/password/forgot', { email }, false);
    
    if (apiData.isSuccess()) {
      logger.info('Password reset request successful', { email });
      return { ok: true, message: apiData.getMessage() };
    }
    
    const error = apiData.getError();
    logger.warn('Password reset request failed', { email, error: error.message });
    return { ok: false, error: error.message, code: error.code };
  } catch (apiData) {
    const error = apiData.getError();
    logger.error('Password reset request threw exception', error, { email });
    return { ok: false, error: error.message, code: error.code };
  }
}

async function requestPasswordHelp(emailOrUsername, requestType = null, phoneNumber = null) {
  try {
    logger.info('Requesting password help', { emailOrUsername, requestType });
    
    const payload = { emailOrUsername };
    if (requestType) payload.requestType = requestType;
    if (phoneNumber) payload.phoneNumber = phoneNumber;
    
    const apiData = await apiService.post('/api/v1/auth/password/help', payload, false);
    
    if (apiData.isSuccess()) {
      logger.info('Password help request successful', { emailOrUsername });
      return { ok: true, message: apiData.getMessage() };
    }
    
    const error = apiData.getError();
    logger.warn('Password help request failed', { emailOrUsername, error: error.message });
    return { ok: false, error: error.message, code: error.code };
  } catch (apiData) {
    const error = apiData.getError();
    logger.error('Password help request threw exception', error, { emailOrUsername });
    return { ok: false, error: error.message, code: error.code };
  }
}

async function resetPassword(token, newPassword) {
  try {
    logger.info('Resetting password with token');
    
    const apiData = await apiService.post('/api/v1/auth/password/reset', { token, newPassword }, false);
    
    if (apiData.isSuccess()) {
      logger.info('Password reset successful');
      return { ok: true, message: apiData.getMessage() };
    }
    
    const error = apiData.getError();
    logger.warn('Password reset failed', { error: error.message });
    return { ok: false, error: error.message, code: error.code };
  } catch (apiData) {
    const error = apiData.getError();
    logger.error('Password reset threw exception', error);
    return { ok: false, error: error.message, code: error.code };
  }
}

export { getCurrentUser, updateProfile, logoutUser, requestPasswordReset, requestPasswordHelp, resetPassword };
